/**
 * PMC Linked Programmes tab API calls with React Query.
 * Use QUERY_KEYS.linkedProgrammes.root to invalidate everything under this domain.
 */
import {
    GetListOfPMCAuditTrail,
    GetProgramAssignedOrNotAssignedListCancelToken,
    LinkedNetsuiteProductCourseList,
    LinkedNetsuiteProductCourseListFilters,
    getVersionList,
} from '../../services/ProgramService';
import { normaliseOptions } from '../../utils/LinkedProgrammeUtils';
import { appQueryClient } from '../appQueryClient';
import { QUERY_CACHE_PROFILES, QUERY_KEYS } from '../queryConsts';
import { useBaseReactQuery } from './useBaseReactQuery';

/**
 * Invalidates the PMC version list (Versions list cards, and the Linked
 * Programmes "Version" filter, which reads the same query), the Versions
 * list's Notes audit-attribution lookup, the programme/version summary totals
 * (a version being created/deleted can reassign which qualifications count
 * toward each version, e.g. a PMC's first version), and the Version Modules
 * list (a clone copies modules into the new version), after a version is
 * created, duplicated, deleted, or edited.
 */
export function invalidateVersionListQueries() {
    return Promise.all([
        appQueryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.linkedProgrammes.root, QUERY_KEYS.linkedProgrammes.versionList],
            refetchType: "all",
        }),
        appQueryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.linkedProgrammes.root, QUERY_KEYS.linkedProgrammes.versionNotesAudit],
            refetchType: "all",
        }),
        appQueryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.programDetail.root, QUERY_KEYS.programDetail.programmeSummary],
            refetchType: "all",
        }),
        appQueryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.programDetail.root, QUERY_KEYS.programDetail.versionSummary],
            refetchType: "all",
        }),
        appQueryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.versionModules.root],
            refetchType: "all",
        }),
    ]);
}

const EMPTY_DROPDOWN = Object.freeze({
    intake: [],
    campus: [],
    deliveryMethod: [],
    programmeType: [],
    versionFilters: [],
});

const EMPTY_LIST = Object.freeze({
    listData: [],
    linkedQualifications: [],
    totalRows: 0,
});

/**
 * Linked Programmes - filter dropdown options (intake/campus/delivery/programme type/version) for one PMC.
 * @param {string | number} pmcId Programme id.
 * @param {string | number} netsuiteProductId PMC's Netsuite product id.
 * @param {number} [isAssigned=1] 1 = already-linked programmes, 0 = the Link Programmes picker's candidates.
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useLinkedProgrammeFilters(pmcId, netsuiteProductId, isAssigned = 1) {
    const query = useBaseReactQuery(
        [QUERY_KEYS.linkedProgrammes.root, QUERY_KEYS.linkedProgrammes.filters, pmcId, netsuiteProductId, isAssigned],
        async () => {
            const res = await LinkedNetsuiteProductCourseListFilters(pmcId, netsuiteProductId, isAssigned);
            return {
                intake: normaliseOptions(res.data?.yearsAndIntaksFilters),
                campus: normaliseOptions(res.data?.campusFilter),
                deliveryMethod: normaliseOptions(res.data?.intakeTypeFilters),
                programmeType: normaliseOptions(res.data?.subjectTypeFilter),
                versionFilters: normaliseOptions(res.data?.versionFilters),
            };
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.DEFAULT,
            enabled: Boolean(pmcId && netsuiteProductId),
        }
    );

    return {
        ...query,
        data: query.data ?? EMPTY_DROPDOWN,
    };
}

/**
 * Linked Programmes - PMC's version list, for the "Version" filter dropdown/active check.
 * @param {string | number} pmcId Programme id.
 * @returns {DefinedUseQueryResult} React Query result
 */
export function usePmcVersionListForFilters(pmcId) {
    const query = useBaseReactQuery(
        [QUERY_KEYS.linkedProgrammes.root, QUERY_KEYS.linkedProgrammes.versionList, pmcId],
        async () => {
            const res = await getVersionList({ pmc_id: pmcId, key: "name", sort: "asc" });
            return res.data?.getVersionList?.resData ?? [];
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.DEFAULT,
            enabled: Boolean(pmcId),
        }
    );

    return {
        ...query,
        data: query.data ?? [],
    };
}

/**
 * Versions list - latest "Notes changed" audit event per version, for each card's
 * "by {author} · {timestamp}" attribution. Fetched once per programme via the existing
 * PMC audit-trail endpoint (`resource_type: "Version"`, unpaginated, newest first), then
 * reduced client-side to the most recent row per version whose diffed `new_data` contains
 * a `notes` key - `audit_trail_PMC` has no "field changed" column, so diffing is the only
 * way to isolate a Notes-specific change from other fields changing in the same save.
 * @returns {DefinedUseQueryResult} React Query result; `data` is a `Map<versionId, {author, updatedAt}>`.
 */
export function useVersionNotesAudit(pmcId) {
    const query = useBaseReactQuery(
        [QUERY_KEYS.linkedProgrammes.root, QUERY_KEYS.linkedProgrammes.versionNotesAudit, pmcId],
        async () => {
            const res = await GetListOfPMCAuditTrail({
                action_id: pmcId,
                resource_type: ["Version"],
                key: "created_at",
                sort: "DESC",
                exportStatus: "true",
            });

            const rows = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
            const notesAuditByVersionId = new Map();

            rows.forEach((row) => {
                const versionId = row.nested_action_id ? String(row.nested_action_id) : null;

                if (!versionId || notesAuditByVersionId.has(versionId)) {
                    return;
                }

                let changedNewData = {};
                try {
                    changedNewData = row.new_data ? JSON.parse(row.new_data) : {};
                } catch (error) {
                    console.error("QueryHooksLinkedProgrammes.js->useVersionNotesAudit->Error", error);
                    changedNewData = {};
                }

                if (!Object.prototype.hasOwnProperty.call(changedNewData, "notes")) {
                    return;
                }

                const firstName = (row.Firstname || "").trim();
                const lastName = (row.Lastname || "").trim();
                const author = firstName ? `${firstName.charAt(0)}. ${lastName}`.trim() : (lastName || null);

                notesAuditByVersionId.set(versionId, {
                    author,
                    updatedAt: row.created_at,
                });
            });

            return notesAuditByVersionId;
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.DEFAULT,
            enabled: Boolean(pmcId),
        }
    );

    return {
        ...query,
        data: query.data ?? new Map(),
    };
}

/**
 * Linked Programmes - main paginated/filtered list of qualifications linked to a PMC.
 * `refreshSignal` isn't sent to the API - it's included in the query key only, so bulk
 * actions elsewhere (lock/unlock/unlink/set version) can force a refetch by changing it.
 * @param {object} requestData Request payload (programme_id, netsuite_product_id, isAssigned, filters, pagination).
 * @param {*} refreshSignal Cache-busting value bumped after a mutation to force a refetch.
 * @param {boolean} [enabled=true] Skip the request until the PMC detail has loaded.
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useLinkedProgrammeList(requestData, refreshSignal, enabled = true) {
    const query = useBaseReactQuery(
        [QUERY_KEYS.linkedProgrammes.root, QUERY_KEYS.linkedProgrammes.list, requestData, refreshSignal],
        async () => {
            const res = await LinkedNetsuiteProductCourseList(requestData);
            return {
                listData: res.data?.data?.data ?? [],
                linkedQualifications: res.data?.allQualificationIds ?? [],
                totalRows: res.data?.data?.total ?? 0,
            };
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.TABLE,
            enabled,
        }
    );

    return {
        ...query,
        data: query.data ?? EMPTY_LIST,
    };
}

/**
 * Version Modules - the modules linked (or not yet linked) to one PMC version, unpaginated (matches the
 * existing `getProgramAssignedOrNotAssignedList` endpoint's current behaviour).
 * Refetches on mount - the shared hook defaults to refetchOnMount false, which would
 * otherwise show pre-mutation data on navigating back after an assign/unlink.
 * @param {string | number} programmeId PMC programme id.
 * @param {string | number} versionId Version id to scope modules to.
 * @param {"assigned" | "available"} [type="assigned"] The version's modules, or the Assign Module modal's candidates.
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useVersionModulesList(programmeId, versionId, type = "assigned") {
    const query = useBaseReactQuery(
        [QUERY_KEYS.versionModules.root, QUERY_KEYS.versionModules.list, programmeId, versionId, type],
        async () => {
            const res = await GetProgramAssignedOrNotAssignedListCancelToken({
                PId: programmeId,
                type,
                version_id: versionId,
            });
            return res.data?.programs ?? [];
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.TABLE,
            refetchOnMount: true,
            enabled: Boolean(programmeId && versionId),
        }
    );

    return {
        ...query,
        data: query.data ?? [],
    };
}
